'use client'

import { useAI } from '@/context/AIContext'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { DynamicWidgetRenderer } from './DynamicWidgetRenderer'

// ============================================================================
// Widget Canvas Pane Component
// ============================================================================

export function WidgetCanvasPane() {
  const {
    activeWidget,
    setActiveWidget,
    setRightPaneOpen,
  } = useAI()

  // Handle close
  const handleClose = () => {
    setActiveWidget(null)
    setRightPaneOpen(false)
  }

  // If no widget, show empty state
  if (!activeWidget || !activeWidget.should_render) {
    return (
      <div className="flex flex-col h-full bg-gray-50/50">
        <div className="flex items-center justify-between p-4 border-b border-border/30 bg-white">
          <h3 className="text-sm font-semibold text-brand-navy">
            Data Deck
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="h-7 w-7"
          >
            <Icons.close className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className={cn(
            'mb-4 flex h-16 w-16 items-center justify-center rounded-2xl',
            'bg-gradient-to-br from-brand-cornflower/10 to-brand-purple/10',
            'ring-1 ring-brand-cornflower/20'
          )}>
            <Icons.pieChart className="h-8 w-8 text-brand-cornflower" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-gray-700">
            No Visualization Active
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Widgets will appear here when the AI returns complex data
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50/50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/30 bg-white">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-brand-navy truncate">
            {activeWidget.title || 'Visualization'}
          </h3>
          {activeWidget.description && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {activeWidget.description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="h-7 w-7 ml-2"
          title="Close widget pane"
        >
          <Icons.close className="h-4 w-4" />
        </Button>
      </div>

      {/* Widget Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="bg-white rounded-lg border border-border/30 shadow-sm">
          {/* Render the widget using existing DynamicWidgetRenderer */}
          <DynamicWidgetRenderer
            components={[
              {
                type: activeWidget.type || 'InfoCard',
                title: activeWidget.title,
                data: activeWidget.data as Record<string, unknown>,
              },
            ]}
          />
        </div>

        {/* Widget Metadata */}
        <div className="mt-4 p-3 bg-white rounded-lg border border-border/30">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Icons.clock className="h-3 w-3" />
            <span>Generated just now</span>
          </div>
          {activeWidget.config && (
            <div className="mt-2 text-xs text-gray-400">
              <span className="font-mono">
                {activeWidget.type || 'WIDGET'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-border/30 bg-white">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5"
            onClick={() => {
              // TODO: Implement download functionality
              console.log('Download widget data')
            }}
          >
            <Icons.download className="h-3 w-3" />
            <span>Export</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5"
            onClick={() => {
              // TODO: Implement refresh functionality
              console.log('Refresh widget')
            }}
          >
            <Icons.refresh className="h-3 w-3" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
